import { useState } from 'react';
import {
    Avatar,
} from '@material-ui/core';
import { Icon as MdiIcon } from '@mdi/react';
import { mdiLicense } from '@mdi/js';
import {
    SwapHoriz,
} from '@material-ui/icons';
import './ShiftLineReportTableMobile.scss';
import {
    DaysOfWeekNames,
    PeriodType,
} from '../../Enums';
import timeService from '../../services/TimeService';
import ShiftTemplateSegmentService from '../../services/ShiftTemplateSegmentService';
import DetailedSchedulePattern from './DetailedSchedulePattern';

/**
 * @param {{
 *  shiftLines: EmployeeBidProfile.IGroupedScheduleTemplateItems[],
 *  shiftsDictionary: BidProfile.ISchedulePatternsShiftTemplateDictionary,
 *  employeeSelections?: string[],
 *  anchorDay: DayOfWeek,
 *  showAwarding?: boolean,
 *  showOrder?: boolean,
 *  isMultiSelection?: boolean,
 *  t: any,
 *  maxNbWeek: number,
 *  daysPerPeriod: number,
 *  dayCount: number,
 *  periodType: PeriodType,
 * }} props
 */
function ShiftLineReportTableMobile({
    shiftLines, shiftsDictionary, employeeSelections, anchorDay, showAwarding = true, showOrder = true, isMultiSelection = true,
    maxNbWeek = 1, t, daysPerPeriod = 7, dayCount, periodType,
}) {
    const [showMoreDetails, setShowMoreDetails] = useState(false);
    const [currentShiftDetailed, setCurrentShiftDetailed] = useState(shiftLines[0]);
    /**
    * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems} selectedSchedule
    */
    const handleOpenDetails = (selectedSchedule) => {
        setShowMoreDetails(true);
        setCurrentShiftDetailed(selectedSchedule);
    };
    /**
     * @param {string} groupId
     * @returns {string}
     */
    const getOrderValue = (groupId) => {
        const index = (employeeSelections || []).findIndex((x) => x === groupId);
        /**
         * @type {any}
        */
        let avatarLabel = '';
        if (index !== -1) {
            avatarLabel = isMultiSelection ? index + 1 : '✓';
        }
        return avatarLabel;
    };
    /**
     * @param {string} shiftTemplateId
     * @returns {BidProfile.IShiftTemplate}
     */
    const getShiftTemplate = (shiftTemplateId) => shiftsDictionary[shiftTemplateId];

    /**
     * @param {number} week
     * @param {number} dayIndex
     * @param {number} shiftOrder
     * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems} schedule
     */
    const generateDay = (week, dayIndex, shiftOrder, schedule) => {
        const isPeriodTypeDay = periodType === PeriodType.Days;
        const labelDayIndex = isPeriodTypeDay ? dayIndex : (dayIndex + anchorDay) % daysPerPeriod;

        const currentShift = schedule.shifts[week] && schedule.shifts[week][dayIndex][shiftOrder];
        const shiftTemplate = currentShift && getShiftTemplate(currentShift.shiftTemplate);
        const numberShiftsInDay = currentShift ? schedule.shifts[week][dayIndex].length : 0;
        const isDayOutsidePeriod = (week * daysPerPeriod) + (dayIndex + 1) > dayCount;

        return (
            <div className="srtm-dayContainer srtm-grayscale">
                {
                    !week ? (
                        <div className="srtm-dayOfWeekLabel">
                            {isPeriodTypeDay ? labelDayIndex + 1 : DaysOfWeekNames[labelDayIndex].substring(0, 2)}
                        </div>
                    ) : <></>
                }
                {
                    (currentShift && schedule.shifts[week][dayIndex].length)
                        ? (
                            <div className="srtm-shiftHoursContainer shiftSquareContainer">
                                {timeService.calculateShiftTimes(shiftTemplate.startTime, shiftTemplate.endTime)}
                                {numberShiftsInDay > 1 && <span className="numberShiftsDay">{` +${numberShiftsInDay - 1}`}</span>}
                                {ShiftTemplateSegmentService.hasTransfer(shiftTemplate.segments) && <SwapHoriz />}
                            </div>
                        )
                        : (
                            // eslint-disable-next-line max-len
                            <div className={`srtm-dayContainer srtm-shiftSquareContainer emptyDay shiftHoursLabel ${isDayOutsidePeriod ? 'srtm-lightgray' : ''}`}>
                                <div className="blueSquareHeight" />
                            </div>
                        )
                }
            </div>
        );
    };
    /**
     * @param {number} weekIndex
     * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems} daySchedule
     */
    const generateWeek = (weekIndex, daySchedule) => {
        const weekShifts = [<></>];
        for (let index = 0; index < 7; index++) {
            weekShifts.push(generateDay(weekIndex, index, 0, daySchedule));
        }
        return (
            <div className="srtm-weekContainer">
                <div className="srtm-weeklyShiftsContainer">
                    {weekShifts}
                </div>
            </div>
        );
    };
    /**
     * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems} schedule
     */
    const scheduleRow = (schedule) => {
        const generatedWeeks = [<></>];
        for (let weeks = 0; weeks < schedule.weeklyHeight.length; weeks++) {
            generatedWeeks.push(generateWeek(weeks, schedule));
        }
        return (
            <div>
                <div className="srtm-shiftTitleRowContainer">
                    <div className="srtm-shiftTitleRow">
                        <div className="srtm-leftSide">
                            <div className="srtm-firstRow">
                                {
                                    showAwarding && schedule.approvedRow ? (
                                        <span>
                                            <MdiIcon
                                                path={mdiLicense}
                                                size={1.0}
                                                color="#00862c"
                                            />
                                        </span>
                                    ) : null
                                }
                                <div className="srtm-shiftTitleText">
                                    {schedule.schedulePatternTemplate.name}
                                </div>
                                {schedule.quantity > 1 && <Avatar className="srtm-group-quantity-avatar">{schedule.quantity}</Avatar>}
                            </div>
                            {schedule.workplanName && (
                                <span className="srtm-cell-text-content text-md-regular">
                                    {schedule.workplanName}
                                </span>
                            )}
                        </div>
                        <div className="srtm-rightSide">
                            <span className="srtm-cell-text-content text-md-regular">{schedule.orgJobPath}</span>
                            {showOrder && (
                                <div className="srtm-avatar-container-centered">
                                    <Avatar className="srtm-summary-table-order-avatar">
                                        {getOrderValue(schedule.groupId)}
                                    </Avatar>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {generatedWeeks}
                <div
                    className="srtm-showMoreText"
                    onClick={() => handleOpenDetails(schedule)}
                >
                    {t('employee.summary.viewMoreInfo')}
                </div>
            </div>
        );
    };
    return (
        <>
            {
                shiftLines.map((schedule) => (
                    <div
                        className="srtm-shiftContainer"
                        key={schedule.id}
                    >
                        {scheduleRow(schedule)}
                    </div>
                ))
            }
            {
                shiftLines?.length ? (
                    <DetailedSchedulePattern
                        isOpen={showMoreDetails}
                        currentShiftDetailed={currentShiftDetailed}
                        shiftsDictionary={shiftsDictionary}
                        onClose={() => setShowMoreDetails(false)}
                        anchorDay={anchorDay}
                        maxNbWeek={maxNbWeek}
                        dayCount={dayCount}
                        periodType={periodType}
                        daysPerPeriod={daysPerPeriod}
                    />
                ) : <></>
            }
        </>
    );
}

export default ShiftLineReportTableMobile;
